/**
 * HxVideoTile Component
 *
 * Displays a single telehealth call participant's video feed (or avatar fallback when camera is off). Overlays the
 * participant name, a muted-microphone badge, a speaking ring, and an optional pin indicator.
 *
 * Intended to be composed in a participant grid for multi-party calls.
 *
 * @example
 *   ```html
 *   <hx-video-tile
 *     participantName="Dr. Sarah Chen"
 *     participantInitials="SC"
 *     [isSpeaking]="true"
 *     [isMuted]="false"
 *   />
 *   ```;
 *
 * @example
 *   Camera-off fallback with avatar
 *   ```html
 *   <hx-video-tile
 *   participantName="John Patient"
 *   participantInitials="JP"
 *   [isVideoOff]="true"
 *   [isLocal]="true"
 *   />
 *   ```
 *
 * @package @complexcare/harmony
 */

import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewEncapsulation,
  computed,
  effect,
  input,
  output,
  viewChild,
} from '@angular/core';
import { AvatarSize, HxAvatar } from '../avatar/avatar';
import { HxIcon } from '../icon/icon';
import { VideoTileAspectRatio, VideoTileSize } from './video-tile.types';

@Component({
  selector: 'hx-video-tile',
  standalone: true,
  imports: [HxAvatar, HxIcon],
  templateUrl: './video-tile.html',
  styleUrls: ['./video-tile.css'],
  host: {
    class: 'hx-video-tile',
    '[class]': 'hostClasses()',
    '[attr.aria-label]': 'ariaLabel()',
    role: 'img',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxVideoTile {
  /** Display name of the participant */
  readonly participantName = input.required<string>();

  /** Initials to show in avatar fallback when camera is off */
  readonly participantInitials = input<string>('');

  /** URL for the participant's avatar image (avatar fallback when camera is off) */
  readonly avatarSrc = input<string | null>(null);

  /** Whether the participant's microphone is muted */
  readonly isMuted = input<boolean>(false);

  /** Whether the participant's camera is off */
  readonly isVideoOff = input<boolean>(false);

  /** Whether the participant is currently speaking (activates speaking ring) */
  readonly isSpeaking = input<boolean>(false);

  /** Whether this tile is pinned/spotlighted */
  readonly isPinned = input<boolean>(false);

  /** Whether this tile represents the local user (shows "You" label) */
  readonly isLocal = input<boolean>(false);

  /** Whether connection is poor for this participant */
  readonly isConnectionPoor = input<boolean>(false);

  /**
   * ACS live-stream element (`VideoStreamRendererView.target`). When provided and `isVideoOff` is false the element is
   * mounted inside the video area so the live video is rendered.
   */
  readonly videoElement = input<HTMLElement | null>(null);

  /** Tile size */
  readonly size = input<VideoTileSize>('md');

  /** Aspect ratio – uses CSS slash notation, e.g. '16/9' */
  readonly aspectRatio = input<VideoTileAspectRatio>('16/9');

  /** Emitted when the tile is clicked (e.g., to pin) */
  readonly tileClick = output<void>();

  private readonly videoAreaRef = viewChild<ElementRef<HTMLDivElement>>('videoArea');

  constructor() {
    // Mount / unmount the ACS stream element whenever the input or video-off state changes
    effect(() => {
      const area = this.videoAreaRef()?.nativeElement;
      const el = this.videoElement();
      const videoOff = this.isVideoOff();

      if (!area) return;

      console.log(`[hx-video-tile effect] Running. el:`, el, `videoOff:`, videoOff, `area:`, area);
      if (el && !videoOff) {
        if (!area.contains(el)) {
          console.log(`[hx-video-tile effect] Appending element to area.`);
          area.innerHTML = '';
          el.classList.add('hx-video-tile__acs-stream');
          area.appendChild(el);
        } else {
          console.log(`[hx-video-tile effect] Element already in area. Ignoring.`);
        }
      } else {
        console.log(`[hx-video-tile effect] Clearing area. videoOff is true or el is null.`);
        area.innerHTML = '';
      }
    });
  }

  protected readonly ariaLabel = computed(() => {
    const name = this.participantName();
    const states: string[] = [];
    if (this.isMuted()) states.push('muted');
    if (this.isVideoOff()) states.push('camera off');
    if (this.isSpeaking()) states.push('speaking');
    if (this.isLocal()) states.push('you');
    return states.length ? `${name} – ${states.join(', ')}` : name;
  });

  protected readonly hostClasses = computed(() => ({
    [`hx-video-tile--${this.size()}`]: true,
    'hx-video-tile--speaking': this.isSpeaking(),
    'hx-video-tile--pinned': this.isPinned(),
    'hx-video-tile--video-off': this.isVideoOff(),
  }));

  /** First two capital letters from the name if initials not supplied */
  protected readonly displayInitials = computed(() => {
    if (this.participantInitials()) return this.participantInitials();
    return this.participantName()
      .split(' ')
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('');
  });

  protected readonly avatarSize = computed((): AvatarSize => {
    const map: Record<VideoTileSize, AvatarSize> = {
      sm: 'sm',
      md: 'lg',
      lg: '2xl',
      full: 'md',
    };
    return map[this.size()];
  });
}
